/* eslint-disable parog-web/migrate-non-patch-forms */
import { set } from "@ember/object";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import dayjs from "dayjs";
import type { EmberChangeset } from "ember-changeset";
import { Changeset } from "ember-changeset";
import { task } from "ember-concurrency";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import type { ForcModelPojo } from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import type ComorbiditiesAndAllergiesChangedJournalEvent from "parog-web/models/comorbidities-and-allergies-changed-journal-event";
import type JournalEventModel from "parog-web/models/journal-event";
import ParogMedicalHistoryChangedJournalEvent from "parog-web/models/medical-history-changed-journal-event";
import type Patient from "parog-web/models/patient";
import type PatientEnrolledJournalEvent from "parog-web/models/patient-enrolled-journal-event";
import type TreatmentPrescriptionChangedJournalEvent from "parog-web/models/treatment-prescription-changed-journal-event";
import Vaccine from "parog-web/models/vaccine";
import type AnalyticsService from "parog-web/services/analytics";
import type FetcherService from "parog-web/services/fetcher";
import loadBioExamFrequencyLoincs from "parog-web/utils/load-bio-exam-frequency-loincs";

export interface Args extends ForcBaseArgs {
  patient: Patient;
}

export interface EnrollmentChangeset extends EmberChangeset {
  followUpReason: string;
  patient: Patient;
}
/**
 * This component contains the shared logic about the medical history.
 * So that when defining the edition form for a parog, we just need to setup
 * the `journalEventProperties` array, containing the `sharedProperties` and
 * the parog specfifc ones.
 */
export default class BaseMedicalHistoryFormComponent extends JournalEventBasedComponent<Args> {
  static override forcDisplayName = "Profil médical";
  // ABSTRACT
  static override fullComponentId = "medical-history/edition-form";
  static override journalEventClass = ParogMedicalHistoryChangedJournalEvent;

  @service declare analytics: AnalyticsService;
  @service declare fetcher: FetcherService;

  @tracked vaccinesAreDirty;

  sharedProperties = ["occurredAt", "vaccines", "comments"];

  get comorbiditiesChangeset(): EmberChangeset {
    const comorbiditiesChangeset = Changeset(this.comorbiditiesModel);

    Object.defineProperty(this, "comorbiditiesChangeset", {
      value: comorbiditiesChangeset,
      writable: false
    });

    return this.comorbiditiesChangeset;
  }

  get comorbiditiesModel(): ComorbiditiesAndAllergiesChangedJournalEvent {
    const lastComorbiditiesJe =
      this.patient.comorbiditiesAndAllergiesChangedJournalEvent;

    if (!lastComorbiditiesJe) {
      return this.store.createRecord(
        "comorbidities-and-allergies-changed-journal-event",
        {
          occurredAt: new Date()
        }
      );
    }

    return lastComorbiditiesJe;
  }

  get enrollmentChangeset(): EnrollmentChangeset {
    const enrollmentChangeset = Changeset(this.enrollmentModel);

    Object.defineProperty(this, "enrollmentChangeset", {
      value: enrollmentChangeset,
      writable: false
    });
    return this.enrollmentChangeset;
  }

  get enrollmentModel(): PatientEnrolledJournalEvent {
    return this.patient
      .patientEnrolledJournalEvent as PatientEnrolledJournalEvent;
  }

  override get isDirty(): boolean {
    if (this.patient.medicalHistoryChangedJournalEvent) {
      return (
        this.vaccinesAreDirty ||
        this.comorbiditiesChangeset.isDirty ||
        this.treatmentsChangeset.isDirty ||
        this.enrollmentChangeset.isDirty ||
        super.isDirty
      );
    }

    return true;
  }

  get patho(): string | undefined {
    const fur = this.enrollmentChangeset.followUpReason as string;
    return this.store.peekRecord("follow-up-reason", fur)?.patho;
  }

  get shouldShowDateLabel(): boolean | undefined {
    const vaccines = this.changeset.get("vaccines");
    return vaccines?.some(vaccine => vaccine.done);
  }

  get treatmentsChangeset(): EmberChangeset {
    const treatmentsChangeset = Changeset(this.treatmentsModel);

    Object.defineProperty(this, "treatmentsChangeset", {
      value: treatmentsChangeset,
      writable: false
    });

    return this.treatmentsChangeset;
  }

  get treatmentsModel(): TreatmentPrescriptionChangedJournalEvent {
    const lastTreatmentPrescriptionChangedJournalEvent =
      this.patient.lastTreatmentPrescriptionChangedJournalEvent;

    if (!lastTreatmentPrescriptionChangedJournalEvent) {
      return this.store.createRecord(
        "treatment-prescription-changed-journal-event",
        {
          occurredAt: new Date(),
          ignoredAtcCodes: [],
          atc: []
        }
      );
    }
    return lastTreatmentPrescriptionChangedJournalEvent;
  }

  override onCancel = task(async () => {
    this.analytics.trackAction([
      "Bouton Cliqué",
      { button_name: "Profil médical - Fermer" }
    ]);
    await this.afterCancel();
  });

  /**
   * HACK: these models should be part of the payload response
   * when saving the "medical-history-changed-journal-event"
   *
   * Fetch Jes to retrieve up-to-date config
   */
  async refreshPps() {
    await this.store.findRecord("patient", this.patient.id, {
      reload: true,
      include: ["constant-monitoring-changed-journal-event"].join(",")
    });

    await this.fetcher.loadRecords(
      "consultation-frequency-changed-journal-event",
      { filter: { patient_id: this.patient.id } }
    );

    // Unload all bio-exam-frequency-changed-journal-events as they may have been
    // archived when calling the Profiler in the back side
    this.store.unloadAll("bio-exam-frequency-changed-journal-event");

    const bioExamFqcyJes = await this.fetcher.loadRecords(
      "bio-exam-frequency-changed-journal-event",
      { filter: { patient_id: this.patient.id } }
    );

    await this.fetcher.loadRecords(
      "medical-act-frequency-changed-journal-event",
      { filter: { patient_id: this.patient.id } }
    );

    // Fetch bio-exam-orders from bio-exam-frequency-changed-journal-events as some orders
    // may have been added
    await loadBioExamFrequencyLoincs(this.store, bioExamFqcyJes);
  }

  override retrieveJournalEvent = () =>
    this.args.patient.medicalHistoryChangedJournalEvent;

  override retrieveModel(): ForcModelPojo {
    const model = super.retrieveModel();

    if (this.isNewMode) {
      model.occurredAt = new Date();
    }

    if (!model.vaccines) {
      model.vaccines = [
        new Vaccine("pneumococcus"),
        new Vaccine("dtPolio"),
        new Vaccine("yellowFever"),
        new Vaccine("seasonalFlu"),
        new Vaccine("sarsCov2")
      ];
    }

    return model;
  }

  override async saveModel(): Promise<JournalEventModel> {
    if (this.comorbiditiesChangeset.isDirty) {
      if (this.comorbiditiesModel.get("isNew")) {
        this.comorbiditiesChangeset.set("patient", this.patient);
      }

      await this.comorbiditiesChangeset.save();
    }

    if (this.treatmentsChangeset.isDirty) {
      if (!this.treatmentsChangeset.get("prescriptions")) {
        this.treatmentsChangeset.set("prescriptions", []);
      }
      this.treatmentsChangeset.set("patient", this.patient);
      await this.treatmentsChangeset.save();
    }

    const followUpReason = this.enrollmentChangeset.followUpReason;
    const currentEnrollment = this.patient.patientEnrolledJournalEvent;
    if (followUpReason !== currentEnrollment?.followUpReason) {
      const newEnrollment = this.store.createRecord(
        "patient-enrolled-journal-event",
        { occurredAt: new Date(), patient: this.patient, followUpReason }
      );
      try {
        await newEnrollment.save();
      } catch (error) {
        /** createRecord made the new record the current enrollment of the patient: link the patient back. */
        this.patient.patientEnrolledJournalEvent = currentEnrollment;
        newEnrollment.unloadRecord();
        throw error;
      }
      this.analytics.trackAction([
        "Objet Suivi Mis À Jour",
        {
          patient_id: this.args.patient.id,
          therapeutic_area: this.args.patient.therapeuticalArea?.id,
          follow_up_reason: this.enrollmentChangeset.followUpReason
        }
      ]);
    }

    /* HACK: the payload returned from saving the "medical-history-changed-journal-event"
    should `include the necessary JEs instead of doing this refreshPps logic */
    const model = await super.saveModel();

    this.analytics.trackAction([
      "Profil Médical Sauvegardé",
      { patient_id: this.args.patient.id }
    ]);

    await this.refreshPps();

    return model;
  }

  setDoneProperty = (targetVaccine: Vaccine, value: boolean): void => {
    this.setVaccinationDateProperty(targetVaccine, null);
    this.setVaccinePropertyInChangeset(targetVaccine, "done", value);
  };

  setVaccinationDateProperty = (
    targetVaccine: Vaccine,
    value: Date | null
  ): void => {
    const error = this.validateVaccineDate(value);
    if (error) {
      this.changeset.addError(
        `vaccines.${targetVaccine.name}.vaccinationDate`,
        error
      );
    }
    if (value === null || !error) {
      this.changeset.removeError?.(
        `vaccines.${targetVaccine.name}.vaccinationDate`
      );
    }
    this.setVaccinePropertyInChangeset(targetVaccine, "vaccinationDate", value);
  };

  setVaccinePropertyInChangeset = (
    targetVaccine: Vaccine,
    key: string,
    value: string | boolean | Date | null
  ): void => {
    const vaccines = this.changeset.get("vaccines");
    const vaccine = vaccines.find(
      vaccine => vaccine.name == targetVaccine.name
    );
    const index = vaccines.findIndex(v => v.name === targetVaccine.name);

    set(vaccine, key, value);
    this.vaccinesAreDirty = true;
    this.changeset.set(`vaccines.${index}.${key}`, value);
  };

  validateVaccineDate(value: Date | null): string | void {
    if (!value) return;

    const minDate = new Date("1900-01-01");
    const maxDate = dayjs().endOf("day").toDate();

    if (value < minDate) {
      return "Cette donnée ne peut être déclarée avant 1900.";
    }

    if (value > maxDate) {
      return "Cette donnée ne peut pas être déclarée dans le futur.";
    }
  }
}
